import React from "react";
import { Inbox, Loader2 } from "lucide-react";
import { cn } from "../lib/utils";

/**
 * The ONE empty-state visual — used for "no data", "no results", "empty
 * cart", etc. everywhere in the app. Centers itself both horizontally and
 * vertically within whatever height its parent gives it: pass `fullHeight`
 * on a page-level empty state (fills the available content area below any
 * sticky header/nav) or leave it off for a compact empty state inside a
 * card/table/section.
 *
 * Pattern lifted from the admin catalog pages (banners/categories/brands),
 * which already had the best-looking empty state in the app — this just
 * makes it the single shared implementation instead of three near-identical
 * copies, and extends it everywhere else that used to be a bare line of text.
 */
export default function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  size = "md",           // "sm" (inline/table) | "md" (section) | "lg" (whole page)
  fullHeight = false,     // vertically centers within the parent's full height
  variant = "light",      // "light" (default, cream cards) | "dark" (full-bleed dark screens like Reels/Rider)
  className = "",
  testId,
}) {
  const badge = size === "lg" ? "w-16 h-16 rounded-3xl" : size === "sm" ? "w-11 h-11 rounded-xl" : "w-14 h-14 rounded-2xl";
  const iconCls = size === "lg" ? "w-7 h-7" : size === "sm" ? "w-5 h-5" : "w-6 h-6";
  const titleCls = size === "lg" ? "font-heading text-xl font-extrabold" : size === "sm" ? "text-sm font-bold" : "font-heading text-lg font-extrabold";
  const dark = variant === "dark";

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center",
        fullHeight ? "min-h-[50vh] flex-1" : "py-10 px-6",
        className,
      )}
      data-testid={testId}
    >
      <div className={cn(badge, "flex items-center justify-center shrink-0", dark ? "bg-white/10" : "bg-brand-cream")}>
        <Icon className={cn(iconCls, dark ? "text-white/60" : "text-neutral-400")} strokeWidth={1.75} />
      </div>
      {title && <div className={cn(titleCls, "mt-4", dark && "text-white")}>{title}</div>}
      {description && <div className={cn(size === "sm" ? "text-xs" : "text-sm", "mt-1 max-w-xs", dark ? "text-white/60" : "text-neutral-600")}>{description}</div>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/** Shared loading spinner — replaces the bare "Loading…" text strewn across
 *  the app with a real indicator, centered the same way EmptyState is. */
export function LoadingState({ label = "Loading…", size = "md", fullHeight = false, variant = "light", className = "", testId = "loading-state" }) {
  const spinnerCls = size === "lg" ? "w-8 h-8" : size === "sm" ? "w-5 h-5" : "w-6 h-6";
  const dark = variant === "dark";
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3",
        dark ? "text-white/70" : "text-neutral-500",
        fullHeight ? "min-h-[50vh] flex-1" : "py-10",
        className,
      )}
      data-testid={testId}
    >
      <Loader2 className={cn(spinnerCls, "animate-spin", dark ? "text-white/50" : "text-neutral-400")} />
      {label && <span className="text-sm font-medium">{label}</span>}
    </div>
  );
}
